// src/lib/ai.js
// Phase 37 — extracted from src/App.jsx (lines 2310–2371, 2512–2522, 2719–2777,
// 2964–2980, 10235–10339, 10817–10915 in the post-storage/format/plants/location/
// products state).
//
// Scope: AI/network helpers — proxy.php calls, schedule + colour parsing,
//        plant-add JSON extraction, beacons, default-task fallbacks.
// Public exports: PARSE_MONTH_ABBREVS, PARSE_MONTH_PATTERN, PARSE_TASK_KEYWORDS,
//                 TONE_HINTS, buildSystemPrompt, callAI, defaultTasks,
//                 extractPlantAdd, extractPlantAddBatch, fireAffBeacon,
//                 fireAskBeacon, lookupStaticSchedule, parseColour,
//                 parseSchedule, parseStatusResponse, reprobeAiStatus,
//                 richDefaultTasks.
// Module state: none (PARSE_* and TONE_HINTS are immutable constants).
//
// Pitfall 1 (RESEARCH §): PARSE_TASK_KEYWORDS / PARSE_MONTH_ABBREVS /
//   PARSE_MONTH_PATTERN MUST live in this file (parseSchedule references them).
// Pitfall 2 (RESEARCH §): extractPlantAdd / extractPlantAddBatch were lifted to
//   App.jsx module scope in commit b134434 then moved here.

import STATIC_SCHEDULES from '../data/schedules.json';
import { MONTHS, MONTH_FULL, CURRENT_MONTH, CURRENT_YEAR } from '../constants.js';
import { getApiBase } from './sync.js';
import { PLANT_TYPE_IDS } from '../data/plant-types.js';

// Parse the proxy.php?status=1 response into structured flags
export function parseStatusResponse(d) {
  return {
    serverKeyReady: !!d.ready,
    devModeAllowed: !!d.devMode,
    commercialFeaturesEnabled: d.commercial_features_enabled === true,
    // Phase 40 — auth field surfaced from the Worker /status payload.
    authConfigured: d.auth === 'configured',
  };
}

// Phase 34 / RES-01 / D-04 — On any AI request non-2xx, immediately re-probe
// proxy.php?status=1 and update aiStatus. No periodic polling — keeps idle-tab
// traffic at zero. Probe success but recent in-flight failure -> degraded.
// Probe failure (or ready:false) -> down. Fire-and-forget from call sites.
export async function reprobeAiStatus(setServerKeyReady, setAiStatus, setLastAiFailureAt) {
  // Defensive guard: any call site that hasn't been wired to receive the
  // aiStatusSetters bundle will arrive here with all three setters undefined.
  // The onboarding wizard's AddPlantForm is one such site (OnboardingPlant did
  // not thread the prop through). Without this guard the AI-failure path would
  // throw TypeError on setServerKeyReady(false) and surface to Sentry as a
  // release error during the critical onboarding funnel. Fail closed silently
  // — the global aiStatus remains whatever the last wired call site set it to.
  if (typeof setServerKeyReady !== 'function' || typeof setAiStatus !== 'function') return;
  if (typeof setLastAiFailureAt === 'function') setLastAiFailureAt(Date.now());
  try {
    const r = await fetch(`${getApiBase()}/proxy.php?status=1`);
    const d = await r.json();
    const ready = !!d.ready;
    setServerKeyReady(ready);
    // Probe ok but the most-recent request failed = degraded.
    // Probe says-not-ready = down.
    setAiStatus(ready ? 'degraded' : 'down');
  } catch {
    setServerKeyReady(false);
    setAiStatus('down');
  }
}

// Phase 39 D-01 — X-Tend-Device-Id outbound header source.
// Reads tend:deviceId (Phase 32 KPI-02 anonymous device UUID). Returns empty
// string when localStorage is unavailable (private browsing mode); the Worker
// treats empty as anonymous and falls back to CF-Connecting-IP for rate-limit
// only (no per-deviceId daily quota). Pure-additive — old proxy.php silently
// ignores this header, so rollout is safe in either direction.
function getDeviceId() {
  try { return localStorage.getItem('tend:deviceId') || ''; } catch { return ''; }
}

// Phase 40 Plan 02 Task 3 — Authorization: Bearer header source.
// Reads the Supabase SDK's session storage key (sb-<project-ref>-auth-token,
// per CONTEXT D-09). Returns empty string when no session is present so
// callAI's conditional spread keeps anonymous calls header-clean (the
// Worker treats absent Authorization as anonymous and falls back to
// deviceId-keyed quota counters).
//
// Resolution order:
//   1. Read VITE_SUPABASE_URL → derive project-ref → read sb-<ref>-auth-token.
//   2. Fall back to scanning localStorage for ANY sb-*-auth-token key (handles
//      tests + multi-project dev environments where the env var isn't set).
function getJwt() {
  try {
    const projectRef = (import.meta.env?.VITE_SUPABASE_URL || '')
      .split('//')[1]?.split('.')[0] || '';
    if (projectRef) {
      const raw = localStorage.getItem(`sb-${projectRef}-auth-token`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.access_token) return parsed.access_token;
      }
    }
    // Fallback scan — picks up the first sb-*-auth-token in storage. This
    // matches the SDK's own resolver order and survives test envs where
    // VITE_SUPABASE_URL isn't injected.
    if (typeof localStorage.length === 'number') {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && /^sb-.+-auth-token$/.test(k)) {
          const raw = localStorage.getItem(k);
          if (!raw) continue;
          const parsed = JSON.parse(raw);
          if (parsed?.access_token) return parsed.access_token;
        }
      }
    } else if (localStorage._store && typeof localStorage._store === 'object') {
      // Node test mock — _store is a plain object.
      for (const k of Object.keys(localStorage._store)) {
        if (/^sb-.+-auth-token$/.test(k)) {
          const raw = localStorage._store[k];
          if (!raw) continue;
          const parsed = JSON.parse(raw);
          if (parsed?.access_token) return parsed.access_token;
        }
      }
    }
    return '';
  } catch { return ''; }
}

// Phase 34 / OBS-03 / D-13 — Product-success beacons.
// Fire-and-forget GET to proxy.php?ask=1 / ?aff=1. Dev-mode-suppressed mirror of
// the Phase 32 D-10 beacon precedent — local dev traffic never pollutes the
// daily aggregation that grafana-export.sh ships to Grafana Cloud.
//
// fireAskBeacon(result): result must be the literal 'ok' or 'err'. Anything
// else is a no-op so junk values from a misuse can't end up in logs/ask/.
//
// fireAffBeacon(productId): productId must match the kebab-case shape used in
// AFFILIATE_PRODUCTS / PLANT_SUPPLIERS — same validation regex as the server
// route so a client-side typo is caught before the network call.
export function fireAskBeacon(result) {
  let isDevActive = false;
  try { isDevActive = localStorage.getItem('tend:devmode') === 'true'; } catch {}
  if (isDevActive) return;
  if (result !== 'ok' && result !== 'err') return;
  fetch(`${getApiBase()}/proxy.php?ask=1&result=${result}`, { headers: { 'X-Tend-Device-Id': getDeviceId() } }).catch(() => {});
}

export function fireAffBeacon(productId) {
  let isDevActive = false;
  try { isDevActive = localStorage.getItem('tend:devmode') === 'true'; } catch {}
  if (isDevActive) return;
  if (!productId || !/^[A-Za-z0-9_-]{1,64}$/.test(String(productId))) return;
  fetch(`${getApiBase()}/proxy.php?aff=1&p=${encodeURIComponent(productId)}`, { headers: { 'X-Tend-Device-Id': getDeviceId() } }).catch(() => {});
}

// Module-level constants used by parseSchedule — hoisted to avoid recreation on every call
export const PARSE_TASK_KEYWORDS = {
  prune:["prun","cut back","trim","deadhead","clip","shape","thin","remove dead","remove damaged"],
  feed:["feed","fertilis","fertiliz","nutrient","growmore","potash","nitrogen","manure","compost feed","seaweed"],
  mulch:["mulch","bark","leaf mould","compost around","top dress"],
  water:["water","irrigat","moist","drought"],
  divide:["divid","split clump","lift and separat"],
  deadhead:["deadhead","remove spent","remove fade"],
  "plant/sow":["plant","sow","bulb","seed","pot on","repot"],
};
export const PARSE_MONTH_ABBREVS = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
export const PARSE_MONTH_PATTERN = `(${MONTH_FULL.join("|")}|Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)`;

// Phase 31.1 Plan 10 — UAT2-GAP-05 rewrite. Each hint now carries an
// explicit length/structure directive so Haiku produces materially
// different replies per tier. Baseline phrase "Be concise, practical,
// and friendly" is ALSO removed tier-conditionally (see buildSystemPrompt
// below) to avoid contradicting the beginner tone.
// Legacy D-08.1 invariant preserved: a missing/empty/unknown experience
// value produces NO tone addition AND keeps the stock baseline.
export const TONE_HINTS = {
  beginner: "The user is new to gardening. Use plain English, avoid jargon, and explain the *why* behind each step. Keep explanations short and reassuring — 2 to 3 short paragraphs. End with one small, confidence-building encouragement.",
  some: "The user has some gardening experience. Assume basic familiarity — no need to define terms like deadheading. Keep replies to 1 or 2 short paragraphs; lead with the action, then one line of context.",
  experienced: "The user is an experienced gardener. Be terse; technical terms welcome. Keep answers under 2 sentences unless the question genuinely requires more. Skip reassurance and preamble.",
};

// Pure helper — build the Ask Tend system prompt. Extracted from the inline
// useMemo inside AskTend so the tone-hint injection is unit-testable without
// rendering the component. Baseline prompt text is preserved verbatim; only
// the optional tone line is new (inserted after the UK-conventions sentence,
// before the PLANT ADDITION block).
export function buildSystemPrompt({ plants, areas, areaMap, experience }) {
  const safePlants = Array.isArray(plants) ? plants : [];
  const safeAreas = Array.isArray(areas) ? areas : [];
  const safeAreaMap = areaMap && typeof areaMap === 'object' ? areaMap : {};

  const plantSummary = safePlants.map(p => {
    const locs = (p.locations || [])
      .map(l => `${safeAreaMap[l.areaId] || l.areaId}${l.subArea ? " › " + l.subArea : ""}`)
      .join(", ");
    const taskSummary = Object.entries(p.tasks || {})
      .map(([m, tasks]) => `${MONTHS[parseInt(m) - 1]}: ${tasks.map(t => `[${t.type}] ${t.text}`).join("; ")}`)
      .join(" | ");
    return `${p.name}${p.cultivar ? " " + p.cultivar : ""} (${p.latin || ""}, ${p.type}, ${locs}, ${p.count} specimen${p.count !== 1 ? "s" : ""}): ${taskSummary}`;
  }).join("\n");

  const areaList = safeAreas
    .map(a => `${a.name} (id: ${a.id}, sub-areas: ${a.subAreas.join(", ") || "none"})`)
    .join("; ");

  const toneHint = TONE_HINTS[experience] || '';
  const toneLine = toneHint ? `\n\n${toneHint}` : '';

  // UAT2-GAP-05 (Phase 31.1 Plan 10): tier-conditional baseline — when an
  // experience tone is set, drop the stock "Be concise, practical, and friendly"
  // line (it conflicts with the beginner directive). When experience is empty
  // or unknown, the original baseline is preserved byte-for-byte for legacy
  // users (D-08.1).
  const baselineTone = toneHint
    ? "Answer naturally and match the user's level."
    : "Be concise, practical, and friendly.";

  return `You are Tend, an expert AI garden assistant. Today is ${MONTH_FULL[CURRENT_MONTH-1]} ${CURRENT_YEAR}. You have detailed knowledge of the user's specific garden with ${safePlants.length} plants across ${safeAreas.length} area(s). Answer questions with specific references to their actual plants, their care schedules, and what is relevant right now. ${baselineTone} Use UK English and UK gardening conventions.${toneLine}

PLANT ADDITION — respond with a friendly sentence, then one of these blocks:

Single plant: PLANT_ADD:{"name":"Common Name","cultivar":"cultivar or empty","latin":"Latin name","type":"shrub","count":1,"areaId":"a1","subArea":"Main Border","wiki":"Latin name","_needsAreaConfirm":false}

Multiple plants (2–50): PLANT_ADD_BATCH:[{"name":"...","cultivar":"","latin":"...","type":"shrub","count":1,"areaId":"a1","subArea":"...","wiki":"...","_needsAreaConfirm":false},...]
IMPORTANT: Include ALL requested plants in a single PLANT_ADD_BATCH array (up to 50). Do NOT split across multiple messages.

Area rules: set "_needsAreaConfirm":true when the user has not specified a location or it is ambiguous; otherwise use the correct areaId and subArea from the list. Type must be one of: ${PLANT_TYPE_IDS.join(', ')}.
Only include a block if you are confident the user wants to add plant(s).

Garden areas: ${areaList}

Garden plant list:
${plantSummary}`;
}

// Phase 37 — extractPlantAdd / extractPlantAddBatch (Pitfall 2 — closure-free,
// lifted to App.jsx module scope in commit b134434, now moved here).
export function extractPlantAdd(text) {
  const match = text.match(/PLANT_ADD:(\{.+\})/);
  if (!match) return null;
  try { return JSON.parse(match[1]); } catch { return null; }
}

export function extractPlantAddBatch(text) {
  const match = text.match(/PLANT_ADD_BATCH:(\[[\s\S]+?\])/);
  if (!match) return null;
  try {
    const arr = JSON.parse(match[1]);
    if (!Array.isArray(arr) || arr.length === 0) return null;
    return arr.slice(0, 25); // enforce 25-plant cap
  } catch { return null; }
}

// ─────────────────────────────────────────────────────────────────────────────
// Static schedule lookup — returns {tasks, colour, height, spread} or null
// Lookup chain step 1 (D-14): static DB -> richDefaultTasks -> defaultTasks
// AI injected between steps 1 and 2 for premium users only (D-08, D-10)
export function lookupStaticSchedule(name, latin) {
  const key = (name || '').toLowerCase();
  const entry = STATIC_SCHEDULES[key];
  if (entry) {
    // Normalise month keys from JSON strings to integers
    const tasks = entry.tasks ? Object.fromEntries(
      Object.entries(entry.tasks).map(([k, v]) => [parseInt(k, 10), v])
    ) : {};
    const colour = entry.colour ? Object.fromEntries(
      Object.entries(entry.colour).map(([k, v]) => [parseInt(k, 10), v])
    ) : {};
    return { tasks, colour, height: entry.height || null, spread: entry.spread || null };
  }
  // Secondary lookup by latin name (handles variant common names — Pitfall 3)
  if (latin) {
    const latinEntry = STATIC_SCHEDULES[latin.toLowerCase()];
    if (latinEntry) {
      const tasks = latinEntry.tasks ? Object.fromEntries(
        Object.entries(latinEntry.tasks).map(([k, v]) => [parseInt(k, 10), v])
      ) : {};
      const colour = latinEntry.colour ? Object.fromEntries(
        Object.entries(latinEntry.colour).map(([k, v]) => [parseInt(k, 10), v])
      ) : {};
      return { tasks, colour, height: latinEntry.height || null, spread: latinEntry.spread || null };
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Parse AI schedule response into tasks object {month: [{type,text}]}
// Module-level so both AddPlantForm and AskTend can use it.
export function parseSchedule(text) {
  const tasks={};
  const lines=text.split("\n");
  let currentMonth=null;
  for (const line of lines) {
    const trimmed=line.trim();
    if (!trimmed) continue;
    const mMatch=trimmed.match(new RegExp(`^#{0,3}\\s*(?:\\*{1,2})?${PARSE_MONTH_PATTERN}(?:\\*{1,2})?:?(?:\\*{1,2})?:?\\s*$`,"i"));
    if (mMatch) {
      const mName=mMatch[1].slice(0,3).toLowerCase();
      const idx=PARSE_MONTH_ABBREVS.indexOf(mName);
      if (idx>=0) currentMonth=idx+1;
      continue;
    }
    if (!currentMonth) continue;
    const clean=trimmed.replace(/^[-•*]\s*/,"").replace(/^\d+\.\s*/,"").trim();
    if (!clean||clean.length<10) continue;
    let taskType="note";
    const lower=clean.toLowerCase();
    for (const [tt,kws] of Object.entries(PARSE_TASK_KEYWORDS)) {
      if (kws.some(kw=>lower.includes(kw))) { taskType=tt; break; }
    }
    if (!tasks[currentMonth]) tasks[currentMonth]=[];
    if (!tasks[currentMonth].some(t=>t.text===clean)) tasks[currentMonth].push({type:taskType,text:clean});
  }
  return tasks;
}

// Parse AI response for seasonal colour data.
// Expects a "## Seasonal Colour" section (all prompts now use this format).
// Falls back to legacy "Colours:" header for any older responses.
//   5: flower, #FF91B0, Pink blooms
// Shared AI fetch helper — module-level, exported for testing
// No API key param: server-side config.php is the sole key source (SEC-02)
// 30-second AbortController timeout (D-03); 1 retry on network failure only (D-04)
// Never throws — all errors returned as {ok, data, error, retryable} (D-14)
export async function callAI(opts) {
  const attempt = async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30_000);
    try {
      // Phase 40 — conditional Authorization: Bearer ${getJwt()} header.
      // Anonymous calls keep the existing X-Tend-Device-Id-only shape so the
      // Worker's deviceId-keyed quota path is unchanged for unauthed users.
      const _jwt = getJwt();
      const res = await fetch(`${getApiBase()}/proxy.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tend-Device-Id': getDeviceId(),
          ...(_jwt && { 'Authorization': `Bearer ${_jwt}` }),
        },
        body: JSON.stringify(opts),
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { ok: false, data: null, error: err?.error || `HTTP ${res.status}`, retryable: false };
      }
      const data = await res.json();
      return { ok: true, data, error: null, retryable: false };
    } catch (e) {
      clearTimeout(timer);
      return { ok: false, data: null, error: e.message || 'Network error', retryable: true };
    }
  };
  const first = await attempt();
  if (!first.ok && first.retryable) return await attempt();
  return first;
}

export function parseColour(text) {
  const colour={};
  const sMatch=text.match(/(?:##\s*Seasonal Colou?r(?:s)?|^Colours:)[^\n]*\n([\s\S]*?)(?=\n##\s|\n[A-Z][a-z]+:|$)/im);
  if(!sMatch) return colour;
  for(const line of sMatch[1].split('\n')){
    const m=line.trim().match(/^(\d{1,2}):\s*(flower|foliage|fruit|bark|other)\s*,\s*(#[0-9a-fA-F]{3,6})\s*,\s*(.+)$/i);
    if(m){const mn=parseInt(m[1]);if(mn>=1&&mn<=12)colour[mn]={type:m[2].toLowerCase(),hex:m[3],label:m[4].trim()};}
  }
  return colour;
}

// ── Option B: Richer default tasks by plant type ───────────────────────────────
export function richDefaultTasks(type, name="", latin="") {
  const n=(name+latin).toLowerCase();

  // Named plant overrides — catches common additions
  if (n.includes("yew")||n.includes("taxus")) return {
    2:[{type:"prune",text:"Trim yew topiary or hedging before growth begins — yew responds well to hard pruning, even into old wood. Remove any dead or winter-damaged shoots."}],
    4:[{type:"feed",text:"Apply a balanced slow-release fertiliser around the base. Yew is not a heavy feeder — avoid high-nitrogen feeds that produce soft, disease-prone growth."}],
    6:[{type:"prune",text:"Main annual clip for topiary or formal shapes — yew holds its shape well after one firm cut. Use sharp shears. Remove any reverted plain-green growth from golden cultivars promptly."}],
    8:[{type:"prune",text:"Light tidy trim if needed to sharpen shape after summer growth. Avoid pruning after September — new growth may not harden before frost."}],
    10:[{type:"note",text:"Red berries (arils) developing — attractive to birds, especially thrushes. All parts of yew except the fleshy red aril are highly toxic — do not compost clippings where livestock can access them."}],
    11:[{type:"mulch",text:"Apply a 5 cm mulch of composted bark around the base to retain moisture and suppress weeds."}],
  };
  if (n.includes("box")||n.includes("buxus")) return {
    5:[{type:"prune",text:"First clip of the season after the main flush of growth. Box responds well to regular trimming — use sharp shears and clean tools between plants to reduce blight spread."}],
    8:[{type:"prune",text:"Second clip in late summer to sharpen shape. This is the last trim of the season — avoid cutting after September as new growth won't harden before frost."}],
    4:[{type:"feed",text:"Apply a balanced slow-release fertiliser. Box responds well to a spring feed — do not over-feed with nitrogen as this produces soft growth prone to blight."}],
    6:[{type:"note",text:"Monitor for box blight (brown patches, black streaks on stems) and box caterpillar (defoliation, webbing). Remove affected material promptly. Consider switching to an alternative topiary plant if blight is persistent."}],
    11:[{type:"mulch",text:"Mulch around the base to protect roots over winter."}],
  };
  if (n.includes("rose")||n.includes("rosa")) return {
    2:[{type:"prune",text:"Main annual prune for bush and hybrid tea roses — cut stems back to an outward-facing bud, removing crossing, weak, and dead stems. Aim for an open vase shape."}],
    3:[{type:"feed",text:"Apply a rose fertiliser or balanced granular feed as growth begins. A specialist rose feed applied twice — in spring and again after the first flush — gives the best results."}],
    5:[{type:"feed",text:"Second application of rose fertiliser after the first flush of flowers to promote repeat flowering."}],
    6:[{type:"deadhead",text:"Deadhead repeat-flowering roses regularly — remove spent blooms back to the first leaf with five leaflets. This encourages further flushes."}],
    7:[{type:"deadhead",text:"Continue deadheading through summer. Water at the base, not the foliage — wet leaves encourage blackspot."}],
    10:[{type:"note",text:"Stop deadheading to allow hips to develop on species roses. Reduce feeding. Tidy up fallen leaves to reduce fungal disease overwintering on debris."}],
    11:[{type:"mulch",text:"Apply a deep mulch of garden compost or well-rotted manure around the base to protect roots and feed the plant over winter. Do not prune in autumn — wait until late winter."}],
  };
  if (n.includes("camellia")) return {
    2:[{type:"note",text:"Flower buds opening — protect from frost and early morning sun which can damage frosted buds. A north or west-facing position reduces bud drop."}],
    3:[{type:"note",text:"Peak flowering. Remove spent blooms to keep the plant tidy."}],
    4:[{type:"prune",text:"Prune immediately after flowering — this gives the plant the maximum time to set buds for next year. Remove wayward or crossing stems. Camellias tolerate hard pruning into old wood."}],
    5:[{type:"feed",text:"Apply an ericaceous fertiliser — camellias must have acidic conditions. Feed again in midsummer. Never lime the soil around a camellia."}],
    7:[{type:"feed",text:"Second ericaceous feed. Water during dry spells — drought stress in summer causes bud drop the following spring."}],
    11:[{type:"mulch",text:"Apply a deep mulch of pine bark or leaf mould to maintain soil acidity and protect roots over winter."}],
  };
  if (n.includes("magnolia")) return {
    3:[{type:"note",text:"Flower buds opening — spectacular display before leaves appear on deciduous varieties. Protect from late frosts which can brown the blooms."}],
    4:[{type:"feed",text:"Apply a balanced slow-release fertiliser after flowering. Magnolias resent root disturbance — scatter fertiliser on the surface and water in."}],
    5:[{type:"note",text:"Leaves fully open. Magnolias prefer neutral to slightly acid soil — yellowing leaves indicate iron deficiency on alkaline soil; treat with sequestered iron."}],
    6:[{type:"prune",text:"If pruning is needed, do it now — wounds heal faster in the growing season. Magnolias are best left unpruned if possible; only remove crossing or damaged branches."}],
    11:[{type:"mulch",text:"Apply a deep mulch of leaf mould or bark around the base — this is the most important maintenance task for magnolias, protecting the shallow roots."}],
  };
  if (n.includes("wisteria")) return {
    2:[{type:"prune",text:"Winter prune — cut back all the long whippy growth from summer to 2–3 buds from the base of each side shoot. This is the key pruning that builds up flowering spurs."}],
    4:[{type:"feed",text:"Apply a high-potash fertiliser (e.g. sulphate of potash or tomato feed) — this encourages flower bud production. Avoid high-nitrogen feeds which produce leafy growth at the expense of flowers."}],
    6:[{type:"note",text:"Spectacular flowering. Tie in new growth you want to keep for extending coverage; remove any unwanted stems."}],
    7:[{type:"prune",text:"Summer prune — cut back all new side shoots to 5–6 leaves from the base. This is the second essential pruning that reduces the jungle of growth and promotes flowering spurs for next year."}],
    10:[{type:"note",text:"Leaves falling. Good time to check ties and wall fixings — wisteria becomes very heavy with age."}],
  };
  if (n.includes("hydrangea")) return {
    2:[{type:"prune",text:"Prune mophead and lacecap hydrangeas (H. macrophylla) lightly — remove only dead flower heads and any dead wood. Do not cut into new growth; flower buds form on last year's wood."}],
    4:[{type:"feed",text:"Apply a high-potash fertiliser to encourage flowering. For blue flowers on acid soil, apply aluminium sulphate; for pink flowers add lime."}],
    7:[{type:"note",text:"Peak flowering. Deadhead spent blooms to keep tidy but leave some seedheads for winter interest."}],
    11:[{type:"mulch",text:"Apply a protective mulch around the base before hard frosts. In cold areas, protect with fleece if severe frost forecast."}],
  };
  if (n.includes("clematis")) return {
    2:[{type:"prune",text:"Group 3 clematis (late-flowering, e.g. viticella): cut all stems back hard to 30 cm above ground. Group 2 (large-flowered, e.g. 'Nelly Moser'): remove dead/weak stems only. Group 1 (spring-flowering, e.g. montana): prune immediately after flowering if needed."}],
    3:[{type:"feed",text:"Apply a high-potash fertiliser as growth begins. Clematis are hungry plants — feed regularly through the growing season."}],
    5:[{type:"feed",text:"Monthly high-potash liquid feed through the growing season to support flowering."}],
    6:[{type:"note",text:"Peak flowering for many varieties. Tie in new growth regularly to prevent tangling. Watch for clematis wilt — cut affected stems back to healthy growth below soil level."}],
    10:[{type:"mulch",text:"Mulch the base deeply to protect the crown. Clematis crown should be planted 5–8 cm below soil level — this protects against wilt and frost."}],
  };

  // Fall through to type-based defaults
  return defaultTasks(type);
}

// ── Option B: Base default tasks by type (kept as fallback) ───────────────────
export function defaultTasks(type) {
  switch(type) {
    case "tree":
    case "fruit tree":
      return{3:[{type:"feed",text:"Apply a balanced slow-release fertiliser around the drip line."}],11:[{type:"prune",text:"Remove dead, diseased, or crossing branches whilst fully dormant."},{type:"mulch",text:"Apply a deep mulch of composted bark around the root zone."}]};
    case "shrub":
    case "evergreen shrub":
      return{3:[{type:"prune",text:"Cut back to encourage new growth. Remove dead or damaged stems."}],4:[{type:"feed",text:"Apply a balanced fertiliser around the base."}],11:[{type:"mulch",text:"Mulch around the base before hard frosts arrive."}]};
    case "topiary":
      return{5:[{type:"prune",text:"First clip of the season after the main flush of new growth. Use sharp, clean shears."}],8:[{type:"prune",text:"Second clip to sharpen shape — last trim before autumn."}],4:[{type:"feed",text:"Apply a balanced slow-release fertiliser around the base."}],11:[{type:"mulch",text:"Mulch around the base for winter."}]};
    case "perennial":
      return{3:[{type:"prune",text:"Cut back all old foliage to just above ground level."}],4:[{type:"feed",text:"Apply a balanced granular fertiliser around the crowns."}],9:[{type:"divide",text:"Divide established clumps to maintain vigour. Replant immediately."}]};
    case "fern":
      return{2:[{type:"prune",text:"Remove old fronds before new croziers unfurl."}],5:[{type:"feed",text:"Apply a half-strength balanced liquid feed."}]};
    case "bulb":
      return{9:[{type:"plant/sow",text:"Plant bulbs at a depth of approximately 3× their diameter."}],6:[{type:"note",text:"Allow foliage to die back naturally — do not cut until fully yellowed."}]};
    case "grass":
    case "bamboo":
      return{3:[{type:"prune",text:"Cut back or comb out old growth before new shoots emerge."}],4:[{type:"feed",text:"Apply a balanced slow-release fertiliser."}]};
    case "climber":
      return{2:[{type:"prune",text:"Prune according to flowering time — spring-flowering climbers after flowering; summer/autumn-flowering in late winter."}],4:[{type:"feed",text:"Apply a high-potash fertiliser to encourage flowering."}],10:[{type:"mulch",text:"Mulch around the base and check wall ties."}]};
    case "herb":
      return{4:[{type:"prune",text:"Cut back woody herbs (rosemary, sage, thyme) lightly to encourage bushy new growth. Do not cut into old wood."}],6:[{type:"note",text:"Harvest herbs regularly — frequent picking encourages fresh growth and delays flowering."}],9:[{type:"prune",text:"Final light trim before winter. Protect tender herbs (basil, lemongrass) from frost."}]};
    case "annual":
      return{3:[{type:"plant/sow",text:"Sow seeds under cover in warmth."}],5:[{type:"plant/sow",text:"Plant out after last frost risk has passed."}],7:[{type:"deadhead",text:"Deadhead regularly to prolong flowering."}]};
    default:
      return{4:[{type:"feed",text:"Apply a balanced fertiliser to support new growth."}],11:[{type:"mulch",text:"Mulch around the base for winter protection."}]};
  }
}
